<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('admin.edit_contract_templates') }}
      </oxd-text>

      <oxd-divider />

      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-input-field
            v-model="contractTemplate.contractName"
            :label="$t('general.contract_name')"
            :rules="rules.contractName"
            required
          />
        </oxd-form-row>
        <oxd-form-row>
          <oxd-input-field
            v-model="contractTemplate.version"
            :label="$t('general.version')"
            :rules="rules.version"
            required
          />
        </oxd-form-row>
        <oxd-form-row>
          <oxd-input-field
            v-model="contractTemplate.description"
            type="textarea"
            :label="$t('general.description')"
            :rules="rules.description"
            required
          />
        </oxd-form-row>
        <oxd-form-row>
          <oxd-input-field
            v-model="contractTemplate.isActive"
            type="select"
            :options="statusOptions"
            :label="$t('general.status')"
          />
        </oxd-form-row>
        <oxd-form-row>
          <file-upload-input
            v-model:newFile="contractTemplate.templateAttachmentNew"
            v-model:method="contractTemplate.method"
            :label="$t('general.contract_file')"
            :button-label="$t('general.browse')"
            :file="contractTemplate.templateAttachment"
            :rules="rules.templateAttachment"
            :url="contractTemplate.downloadPath"
            :hint="$t('general.accepts_up_to_1mb')"
            :deletable="false"
            :accept="acceptFile"
          />
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import {
  required,
  validFileTypes,
  maxFileSize,
} from '@ohrm/core/util/validation/rules';
import FileUploadInput from '@/core/components/inputs/FileUploadInput.vue';

const initialData = {
  contractName: '',
  version: '',
  isActive: null,
  description: '',
  templateAttachment: {
    id: null,
    filename: '',
    filetype: '',
    size: '',
  },
  templateAttachmentNew: null,
  downloadPath: '',
  method: 'keepCurrent',
};

export default {
  components: {
    'file-upload-input': FileUploadInput,
  },
  props: {
    allowedFileTypes: {
      type: Array,
      required: true,
    },
    maxFileSize: {
      type: Number,
      required: true,
    },
    contractTemplatesId: {
      type: Number,
      required: true,
    },
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/contract-templates',
    );
    return {
      http,
    };
  },

  data() {
    return {
      isLoading: false,
      contractTemplate: {...initialData},
      rules: {
        contractName: [required],
        version: [required],
        description: [required],
        templateAttachment: [
          required,
          validFileTypes(this.allowedFileTypes),
          maxFileSize(this.maxFileSize),
        ],
      },
      statusOptions: [
        {
          id: 1,
          param: 'active',
          label: this.$t('general.active'),
        },
        {
          id: 2,
          param: 'inactive',
          label: this.$t('general.inactive'),
        },
      ],
      acceptFile:
        '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    };
  },

  created() {
    this.isLoading = true;
    this.http
      .get(this.contractTemplatesId)
      .then((response) => {
        const {data} = response.data;
        const status = data.isActive ? 'active' : 'inactive';
        this.contractTemplate.contractName = data.contractName;
        this.contractTemplate.description = data.description;
        this.contractTemplate.version = data.version;
        this.contractTemplate.isActive =
          this.statusOptions.find((item) => item.param === status) || null;
        this.contractTemplate.templateAttachment.id = data.id;
        this.contractTemplate.templateAttachment.filename = data.fileName;
        this.contractTemplate.templateAttachment.filetype = data.fileType;
        this.contractTemplate.templateAttachment.size = data.size;
        this.contractTemplate.downloadPath = data.downloadPath
          .split('/')
          .filter(Boolean)
          .slice(0, -1)
          .join('/');
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    onCancel() {
      navigate('/admin/contractTemplates');
    },
    hasNewTemplateAttachment() {
      const {method, templateAttachmentNew: attachment} = this.contractTemplate;
      return (
        method === 'replaceCurrent' &&
        !!attachment?.base64 &&
        !!attachment?.name
      );
    },
    onSave() {
      this.isLoading = true;
      this.http
        .update(this.contractTemplatesId, {
          contractName: this.contractTemplate.contractName,
          version: this.contractTemplate.version.toString(),
          isActive: this.contractTemplate.isActive.param === 'active',
          description: this.contractTemplate.description,
          ...(this.hasNewTemplateAttachment() && {
            templateAttachment: this.contractTemplate.templateAttachmentNew,
          }),
        })
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .then(() => {
          this.onCancel();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
